import { motion } from 'framer-motion';
import { TrendingUp, Video, Palette, Target, MapPin, CheckCircle } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <TrendingUp size={48} />,
      title: 'Social Media Management (SMM)',
      description: 'Complete social media management across all major platforms',
      features: [
        'Instagram & Facebook management',
        'Content strategy and calendar',
        'Audience growth strategies',
        'Community management',
        'Regular analytics reports',
      ],
      image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800',
    },
    {
      icon: <Video size={48} />,
      title: 'Video Editing',
      description: 'Professional video editing that captures attention and drives engagement',
      features: [
        'Reels & Shorts editing',
        'Promotional video creation',
        'High engagement social content',
        'Storytelling with voiceover',
        'Hook-based content structure',
      ],
      image: 'https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800',
    },
    {
      icon: <Palette size={48} />,
      title: 'Creative Design',
      description: 'Eye-catching designs that align with your brand identity',
      features: [
        'Social media posts',
        'Ad creatives',
        'Brand visuals',
        'Carousel designs',
        'Story templates',
      ],
      image: 'https://images.unsplash.com/photo-1626785774573-4b799315345d?w=800',
    },
    {
      icon: <Target size={48} />,
      title: 'Meta Ads Management',
      description: 'Data-driven advertising campaigns that deliver results',
      features: [
        'Facebook & Instagram ads',
        'Lead generation campaigns',
        'Conversion optimization',
        'A/B testing',
        'ROI tracking',
      ],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800',
    },
    {
      icon: <MapPin size={48} />,
      title: 'Google My Business (GMB) Optimization',
      description: 'Improve your local search visibility and attract nearby customers',
      features: [
        'Local SEO optimization',
        'Business profile ranking',
        'Profile management',
        'Review management',
        'Regular updates & posts',
      ],
      image: 'https://images.unsplash.com/photo-1523908511403-7fc7b25592f4?w=800',
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="services-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text" data-testid="services-title">
            Our Services
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Comprehensive digital marketing solutions designed to help your business thrive in the digital landscape
          </p>
        </motion.div>

        {/* Services List */}
        <div className="space-y-24">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 items-center`}
              data-testid={`service-${index}`}
            >
              {/* Image */}
              <div className="w-full md:w-1/2">
                <div className="relative rounded-2xl overflow-hidden group">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-96 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
              </div>

              {/* Content */}
              <div className="w-full md:w-1/2 space-y-6">
                <div className="text-[#3B82F6] mb-4">{service.icon}</div>
                <h2 className="text-3xl md:text-4xl font-bold text-white">{service.title}</h2>
                <p className="text-gray-400 text-lg leading-relaxed">{service.description}</p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle size={20} className="text-[#3B82F6] mt-1 flex-shrink-0" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24 text-center glass rounded-2xl p-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-8">
            Choose a package that fits your needs or contact us for a custom solution
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/packages"
              data-testid="services-cta-packages"
              className="bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white px-8 py-4 rounded-full font-semibold hover:shadow-[0_0_30px_rgba(59,130,246,0.6)] transition-all hover:scale-105"
            >
              View Packages
            </a>
            <a
              href="/contact"
              data-testid="services-cta-contact"
              className="bg-white/5 border border-white/10 hover:bg-gradient-to-b from-[#0A0A0A] to-black/10 text-white px-8 py-4 rounded-full font-semibold backdrop-blur-md transition-all"
            >
              Contact Us
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;